import { createPaginator } from '@orange-marmalade/paginate-this';
import { fromJS } from 'immutable';
import { createDuck } from 'redux-duck';
import { findIndex } from 'underscore';

import { PROPERTY_TYPE_MAP, PropertyRecord } from './PropertySearch';

// middleware
import PaginateThisDuckCreator from '../middleware/paginate-this';
import { api, cleanQuery, formatError } from '../middleware/client';

const BASE_URL = `${process.env.REACT_APP_REST_URI}/properties`;
const locator = ({ FavoriteSearch }) => FavoriteSearch.get('list');
const listId = 'favorites';
const defaultQuery = {
  page: 1,
  pageSize: 200,
  propertyClass: 'residential'
};

const paginateReducer = createPaginator({
  listId,
  fetch,
  locator,
  cache: true,
  initialSettings: {
    // initialized is true to prevent extra actions being fired
    // since we actually initialize the list using query parameters
    initialized: true,
    ...defaultQuery
  }
});

const duck = createDuck('FavoriteSearch', 'nabor');
const ABORTED_REQUEST_TYPE = duck.defineType('ABORTED_REQUEST');
const ABORTED_REQUEST = duck.createAction(ABORTED_REQUEST_TYPE);

const ADD_FAVORITE_TYPE = duck.defineType('ADD_FAVORITE');
const REMOVE_FAVORITE_TYPE = duck.defineType('REMOVE_FAVORITE');
const LOAD_FAVORITES_TYPE = duck.defineType('LOAD_FAVORITES');

const ADD_FAVORITE_ACTION = duck.createAction(ADD_FAVORITE_TYPE);
const REMOVE_FAVORITE_ACTION = duck.createAction(REMOVE_FAVORITE_TYPE);
const LOAD_FAVORITES_ACTION = duck.createAction(LOAD_FAVORITES_TYPE);

// custom abort support
var lastRequest;

const initialState = fromJS({
  list: paginateReducer(),
  ids: [],
  favorites: []
});

function reducer(state = initialState, action) {
  var ids, favorites, index;

  switch (action.type) {
    case ABORTED_REQUEST_TYPE:
      state = state.setIn(['list', 'isLoading'], false);
      break;
    case REMOVE_FAVORITE_TYPE:
      ids = state.get('ids');
      favorites = state.get('favorites');
      index = ids.indexOf(action.payload);
      if (index !== -1) {
        ids = [...ids.slice(0, index), ...ids.slice(index + 1)];
        favorites = [...favorites.slice(0, index), ...favorites.slice(index + 1)];
        state = state.merge({
          ids,
          favorites
        });
        if (state.getIn(['list', 'totalCount']) < 0) {
          state = state.setIn(['list', 'totalCount'], 0);
        }
      }
      break;
    case ADD_FAVORITE_TYPE:
      ids = state.get('ids');
      favorites = state.get('favorites');
      index = ids.indexOf(action.payload.id);
      if (index === -1) {
        state = state.merge({
          ids: [...ids, action.payload.id],
          favorites: [...favorites, action.payload]
        });
      }
      break;
    case LOAD_FAVORITES_TYPE:
      favorites = action.payload.data.results;
      ids = favorites.map(favorite => favorite.id);
      state = state.merge({
        ids,
        favorites
      });
      break;
    // Lookup the propertyClass being filtered by, and adjust 'ids' and 'favorites' based off of the returned results
    case '@@paginate-this/RESULTS_UPDATED_favorites':
      // Filter out ids that belong to the propertyClass but were not returned from the fetch
      favorites = state.get('favorites').filter(({ id, propertyClass }) => {
        if (propertyClass !== action.results.meta.propertyClass) {
          return true;
        }

        const idxOfFavorite = action.results.results.findIndex(
          result => result.id === id
        );

        return idxOfFavorite !== -1;
      });

      if (favorites.length !== state.get('favorites').length) {
        state = state.merge({
          ids: favorites.map(favorite => favorite.id),
          favorites
        });
        SAVE_FAVORITES(favorites);
      }
      // We injected a meta property in here and need to restore the orginal setup
      action.results = action.results.results;
    // eslint-disable-next-line
    default:
      state = state.setIn(
        ['list'],
        paginateReducer.call(this, state.get('list'), action)
      );
      break;
  }

  return state;
}

function abortRequest() {
  if (lastRequest && lastRequest.abort) {
    lastRequest.abort();
  }

  return ABORTED_REQUEST(lastRequest);
}

function fetch(params) {
  const { query = {} } = params;
  const { propertyClass = 'residential' } = query;

  return (dispatch, getState) => {
    const state = getState();
    const ids = state.FavoriteSearch.get('favorites')
      .filter(favorite => {
        return favorite.propertyClass === propertyClass;
      })
      .map(favorite => favorite.id)
      .join(',');

    if (ids.length > 0) {
      lastRequest = api.get(BASE_URL, {
        query: cleanQuery({
          ...query,
          page: undefined,
          pageSize: undefined,
          offset: query.pageSize * (query.page - 1),
          limit: query.pageSize,
          view: 'favorite',
          ids
        })
      });
    } else {
      lastRequest = Promise.resolve({
        list: [],
        total: 0
      });
    }

    return lastRequest
      .then(function (response) {
        lastRequest = undefined;
        return {
          data: {
            results: {
              results: response.list.map(recordData =>
                new PropertyRecord(recordData).toJS()
              ),
              meta: {
                propertyClass
              }
            },
            total_count: response.total
          }
        };
      })
      .catch(error => {
        throw formatError(error);
      });
  };
}

function GET_FAVORITES(propertyClassToFilter) {
  var favorites;

  try {
    favorites = JSON.parse(localStorage.getItem('NAPLES_FAVORITES')) || [];
  } catch (e) {
    favorites = [];
  }

  if (propertyClassToFilter) {
    favorites = favorites.filter(
      ({ propertyClass }) => propertyClass === propertyClassToFilter
    );
  }

  return favorites;
}

function REMOVE_FAVORITE(id) {
  return (dispatch, getState) => {
    let favorites = GET_FAVORITES();
    let indexOfFavorite = findIndex(favorites, favorite => favorite.id === id);
    if (indexOfFavorite !== -1) {
      dispatch(paginateThis.pageActions.removeItem(id));
      dispatch(REMOVE_FAVORITE_ACTION(id));
      SAVE_FAVORITES([
        ...favorites.slice(0, indexOfFavorite),
        ...favorites.slice(indexOfFavorite + 1)
      ]);
    }
  };
}

function ADD_FAVORITE({ id, mlsNumber, propertyClass }) {
  return (dispatch, getState) => {
    let favorites = GET_FAVORITES();
    let propertyClassFavorites = GET_FAVORITES(propertyClass);
    let indexOfFavorite = findIndex(favorites, favorite => favorite.id === id);

    if (propertyClassFavorites.length >= 40) {
      alert('Maximum of 10 Favorites per type');
    } else if (indexOfFavorite === -1) {
      dispatch(
        ADD_FAVORITE_ACTION({
          id,
          mlsNumber,
          propertyClass
        })
      );
      dispatch(paginateThis.pageActions.expire());
      SAVE_FAVORITES([
        ...favorites,
        {
          id,
          mlsNumber,
          propertyClass
        }
      ]);
    }
  };
}

function SAVE_FAVORITES(favorites) {
  localStorage.setItem('NAPLES_FAVORITES', JSON.stringify(favorites));
}

function isFavorite(state, id) {
  return state.FavoriteSearch.get('ids').indexOf(id) !== -1;
}

function LOAD_FAVORITES() {
  return (dispatch, getState) => {
    const results = GET_FAVORITES();
    const oldNumFavorites = getState().FavoriteSearch.get('ids').length;

    dispatch(LOAD_FAVORITES_ACTION({ data: { results } }));

    if (results.length !== oldNumFavorites) {
      dispatch(paginateThis.pageActions.expire());
    }
  };
}

function getCountsByPropertyClass(state) {
  const counts = {};
  const favorites = state.FavoriteSearch.get('favorites');

  Object.keys(PROPERTY_TYPE_MAP).forEach(propertyClassToFilter => {
    counts[propertyClassToFilter] = favorites.filter(
      ({ propertyClass }) => propertyClass === propertyClassToFilter
    ).length;
  });

  return counts;
}

const paginateThis = new PaginateThisDuckCreator({
  listId,
  locator,
  defaultQuery,
  abortRequest,
  // Lists filters that will automatically get parsed from the query string
  availableFilters: {
    propertyClass: undefined
  }
});

/*, LOAD_FAVORITES*/
export {
  paginateThis,
  fetch,
  isFavorite,
  REMOVE_FAVORITE,
  ADD_FAVORITE,
  LOAD_FAVORITES,
  getCountsByPropertyClass
};
export default reducer;



// WEBPACK FOOTER //
// ./src/ducks/FavoriteSearch.js